import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "@/config/config";
import {
  cancelRace,
  createRace,
  getNextRaceTime,
  joinRace,
} from "@/helper/contractFunctions/MafiaRaceLobby";
import { getRaceXp } from "@/helper/contractFunctions/MafiaRaceXP";
import type { Race } from "@/types/Racing/Race";

export interface RaceState {
  nextRaceTime: number;
  isCreatingRaceLobby: boolean;
  isJoiningRaceLobby: boolean;
  isCancellingRaceLobby: boolean;
  raceList: Race[];
  isLoadingRaceList: boolean;
  raceXp: number;
}

const initialState: RaceState = {
  nextRaceTime: -1,
  isCreatingRaceLobby: false,
  isJoiningRaceLobby: false,
  isCancellingRaceLobby: false,
  raceList: [],
  isLoadingRaceList: false,
  raceXp: 0,
};

export const getRaceListAction = createAsyncThunk(
  "race/getRaceListAction",
  async ({ status, account }: { status?: number; account?: string }) => {
    const queryParams = new URLSearchParams();
    if (status !== undefined) queryParams.append("status", status.toString());
    if (account) queryParams.append("address", account);

    const response = await axios.get(
      `${SERVER_URL}/race?${queryParams.toString()}`
    );
    return response.data;
  }
);

export const createRaceLobbyAction = createAsyncThunk(
  "race/createRaceLobbyAction",
  async ({ carId, cashAmount, prizeType, signMsg, signature, account }: {
    carId: number;
    cashAmount: number;
    prizeType: number;
    signature: string;
    signMsg: string;
    account: string;
  }) => createRace(carId, cashAmount, prizeType, signature, signMsg, account)
);

export const joinRaceLobbyAction = createAsyncThunk(
  "race/joinRaceLobbyAction",
  async ({ raceId, carId, signature, signMsg, account }: {
    raceId: number;
    carId: number;
    signature: string;
    signMsg: string;
    account: string;
  }) => joinRace(raceId, carId, signature, signMsg, account)
);

export const cancelRaceLobbyAction = createAsyncThunk(
  "race/cancelRaceLobbyAction",
  async ({ raceId, account }: { raceId: number; account: string }) =>
    cancelRace(raceId, account)
);

export const getNextRaceTimeAction = createAsyncThunk(
  "race/getNextRaceTimeAction",
  getNextRaceTime
);

export const getRaceXpAction = createAsyncThunk(
  "race/getRaceXpAction",
  async ({ account, message, signature }: {
    account: string;
    message: string;
    signature: string;
  }) => getRaceXp(account, message, signature)
);

const raceSlice = createSlice({
  name: "race",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRaceListAction.pending, (state) => {
        state.isLoadingRaceList = true;
        state.raceList = [];
      })
      .addCase(getRaceListAction.fulfilled, (state, { payload }) => {
        state.raceList = payload;
        state.isLoadingRaceList = false;
      })
      .addCase(getRaceListAction.rejected, (state, { error }) => {
        state.raceList = [];
        state.isLoadingRaceList = false;
        console.log(error);
      })
      .addCase(createRaceLobbyAction.pending, (state) => {
        state.isCreatingRaceLobby = true;
      })
      .addCase(createRaceLobbyAction.fulfilled, (state) => {
        state.isCreatingRaceLobby = false;
      })
      .addCase(createRaceLobbyAction.rejected, (state, { error }) => {
        state.isCreatingRaceLobby = false;
        console.log(error.message);
      })
      .addCase(joinRaceLobbyAction.pending, (state) => {
        state.isJoiningRaceLobby = true;
      })
      .addCase(joinRaceLobbyAction.fulfilled, (state) => {
        state.isJoiningRaceLobby = false;
      })
      .addCase(joinRaceLobbyAction.rejected, (state, { error }) => {
        state.isJoiningRaceLobby = false;
        console.log(error.message);
      })
      .addCase(cancelRaceLobbyAction.pending, (state) => {
        state.isCancellingRaceLobby = true;
      })
      .addCase(cancelRaceLobbyAction.fulfilled, (state) => {
        state.isCancellingRaceLobby = false;
      })
      .addCase(cancelRaceLobbyAction.rejected, (state, { error }) => {
        state.isCancellingRaceLobby = false;
        console.log(error.message);
      })
      .addCase(getNextRaceTimeAction.fulfilled, (state, { payload }) => {
        state.nextRaceTime = payload;
      })
      .addCase(getNextRaceTimeAction.rejected, (state) => {
        state.nextRaceTime = -1;
      })
      .addCase(getRaceXpAction.fulfilled, (state, { payload }) => {
        state.raceXp = payload;
      })
      .addCase(getRaceXpAction.rejected, (_state, { error }) => {
        console.log(error.message);
      });
  },
});

export default raceSlice.reducer;
